/**
 * Client-side Stripe utilities
 *
 * Note: This file is for client-side usage only.
 * For server-side operations, use lib/stripe.ts
 */

import type { CreditPackageId } from '@/config/stripe-plans.config';

/** A purchase: a credit package or the free-amount top-up. */
export type PurchaseRequestBody =
  | { packageId: CreditPackageId; autoTopup?: boolean; returnPath?: string }
  | { topup: true; quantity?: number; autoTopup?: boolean; returnPath?: string };

/** What POST /api/stripe/create-checkout-session accepts (see the route). */
export type CheckoutRequestBody = PurchaseRequestBody | { setup: 'auto_topup'; returnPath?: string };

async function postCheckoutSession(body: object): Promise<Record<string, unknown>> {
  const response = await fetch('/api/stripe/create-checkout-session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to create checkout session');
  }
  return response.json();
}

/**
 * Start a Stripe Checkout (credit package, free-amount top-up, or saving a
 * card for auto top-up) and redirect to it.
 */
export async function startCheckout(body: CheckoutRequestBody): Promise<void> {
  const { url } = await postCheckoutSession(body);
  if (typeof url === 'string' && url) {
    window.location.href = url;
  }
}

export interface EmbeddedCheckoutSession {
  sessionId: string;
  clientSecret: string;
  publishableKey: string;
}

/**
 * Create the in-page (custom-UI) Checkout Session for the checkout modal (only with
 * NEXT_PUBLIC_STRIPE_EMBEDDED_CHECKOUT=1; the server answers with a hosted
 * session otherwise, which this treats as an error).
 */
export async function createEmbeddedCheckoutSession(
  body: PurchaseRequestBody,
  signal?: AbortSignal,
): Promise<EmbeddedCheckoutSession> {
  const response = await fetch('/api/stripe/create-checkout-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, embedded: true }),
    signal,
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok || typeof json.clientSecret !== 'string' || typeof json.publishableKey !== 'string') {
    throw new Error(json.error || 'Failed to create checkout session');
  }
  return { sessionId: json.sessionId, clientSecret: json.clientSecret, publishableKey: json.publishableKey };
}

/**
 * Create a one-time credit top-up checkout session (1 € / 100 credits,
 * quantity adjustable on the Stripe page) and redirect to Stripe Checkout.
 */
export async function createTopupCheckoutSession(
  /** Euros of credit to buy — one unit is 1 € / 100 credits. The server
   *  clamps it, so a tampered value can't mint a cheaper session. */
  quantity?: number,
  /** Path the Stripe page's back link returns to; defaults to /plans. */
  returnPath?: string,
): Promise<void> {
  return startCheckout({ topup: true, quantity, returnPath });
}

/**
 * Open Stripe Customer Portal
 */
export async function openCustomerPortal(returnUrl?: string): Promise<void> {
  try {
    const response = await fetch('/api/stripe/create-portal-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        returnUrl:
          returnUrl ||
          (typeof window !== 'undefined' ? window.location.origin : '') +
            '/account/billing',
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create portal session');
    }

    const { url } = await response.json();
    if (url) {
      window.location.href = url;
    }
  } catch (error) {
    throw error;
  }
}

/**
 * Switch the checkout modal's open session to another package or amount in
 * place (POST /api/stripe/update-checkout-session). Throws on refusal, so
 * Stripe.js `runServerUpdate` reports it as an error.
 */
export async function updateCheckoutSession(
  sessionId: string,
  body: { packageId: CreditPackageId } | { topup: true; quantity: number },
): Promise<void> {
  const response = await fetch('/api/stripe/update-checkout-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId, ...body }),
  });
  if (!response.ok) {
    const json = await response.json().catch(() => ({}));
    throw new Error(json.code || 'update_failed');
  }
}
